import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TTSOptions {
  voice?: string;
  model?: string;
}

export const useTextToSpeech = () => {
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [audioQueue, setAudioQueue] = useState<HTMLAudioElement[]>([]);

  const speak = async (text: string, options: TTSOptions = {}) => {
    try {
      setIsSpeaking(true);

      // Call edge function
      const { data, error } = await supabase.functions.invoke("text-to-speech", {
        body: {
          text,
          voice: options.voice || "9BWtsMINqrJLrRacOk9x", // Aria - energetic female
          model: options.model || "eleven_turbo_v2_5",
          volume: 1.0, // Maximum volume
        },
      });

      if (error) throw error;

      if (!data?.audioContent) {
        throw new Error("No audio content received");
      }

      // Convert base64 to audio blob
      const binaryString = atob(data.audioContent);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      const audioBlob = new Blob([bytes], { type: "audio/mpeg" });
      const audioUrl = URL.createObjectURL(audioBlob);

      // Create and play audio
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        setAudioQueue((prev) => prev.filter((a) => a !== audio));
        if (audioQueue.length === 0) {
          setIsSpeaking(false);
        }
      };

      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        setAudioQueue((prev) => prev.filter((a) => a !== audio));
        setIsSpeaking(false);
        toast.error("Failed to play audio");
      };

      setAudioQueue((prev) => [...prev, audio]);
      await audio.play();

    } catch (error) {
      console.error("Text-to-speech error:", error);
      toast.error("Failed to generate speech");
      setIsSpeaking(false);
    }
  };

  const stopSpeaking = () => {
    audioQueue.forEach((audio) => {
      audio.pause();
      audio.currentTime = 0;
    });
    setAudioQueue([]);
    setIsSpeaking(false);
  };

  return {
    speak,
    stopSpeaking,
    isSpeaking,
  };
};
